import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign } from "lucide-react";
import type { CloudCostSummary } from "@/stores/cloudStore";

interface CostSummaryCardProps {
  costs: CloudCostSummary | undefined;
}

const SERVICE_COLORS: Record<string, string> = {
  "AWS Lambda": "#F79400",
  "Amazon Simple Storage Service": "#3F8624",
  "Amazon Relational Database Service": "#3B48CC",
  "Amazon Elastic Container Service": "#ED7100",
  "Amazon API Gateway": "#A166FF",
  "Amazon DynamoDB": "#4053D6",
  "Amazon Simple Queue Service": "#FF9900",
  "Amazon CloudFront": "#8C4FFF",
};

function getServiceColor(service: string): string {
  return SERVICE_COLORS[service] || "#6b7280";
}

function shortenServiceName(name: string): string {
  const mapping: Record<string, string> = {
    "AWS Lambda": "Lambda",
    "Amazon Simple Storage Service": "S3",
    "Amazon Relational Database Service": "RDS",
    "Amazon Elastic Container Service": "ECS",
    "Amazon API Gateway": "API Gateway",
    "Amazon DynamoDB": "DynamoDB",
    "Amazon Simple Queue Service": "SQS",
    "Amazon CloudFront": "CloudFront",
    "Amazon Elastic Compute Cloud - Compute": "EC2",
    "Amazon ElastiCache": "ElastiCache",
    "Amazon Simple Notification Service": "SNS",
  };
  return mapping[name] || name;
}

export function CostSummaryCard({ costs }: CostSummaryCardProps) {
  if (!costs || costs.totalMonthly === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Cost Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No cost data available. Click "Refresh Costs" to fetch data from AWS Cost Explorer.
          </p>
        </CardContent>
      </Card>
    );
  }

  const sortedServices = Object.entries(costs.costByService)
    .sort(([, a], [, b]) => b - a);
  const maxCost = sortedServices.length > 0 ? sortedServices[0][1] : 1;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          Cost Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-6">
          <div>
            <p className="text-2xl font-bold">${costs.totalDaily.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">per day</p>
          </div>
          <div>
            <p className="text-2xl font-bold">${costs.totalMonthly.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">per month</p>
          </div>
        </div>

        {sortedServices.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase">Cost by Service</p>
            {sortedServices.map(([service, cost]) => {
              const pct = costs.totalMonthly > 0 ? (cost / costs.totalMonthly) * 100 : 0;
              const barWidth = maxCost > 0 ? (cost / maxCost) * 100 : 0;
              const color = getServiceColor(service);
              return (
                <div key={service} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span>{shortenServiceName(service)}</span>
                    <span className="text-muted-foreground">
                      ${cost.toFixed(2)} ({pct.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${barWidth}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {costs.refreshedAt && (
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(costs.refreshedAt).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
